import MUIDataTableCustomStyle from "../MUIDataTableCustomStyle";
import { Vocabulary } from "../../Utils/Vocabulary";
import { endpoints } from "../../Utils/UrlEnum";
import { Chip, Button, Box } from "@mui/material";
import { ETableEditAction, EWidths } from "../../Utils/Enums";
import genericStyles from "../../Styles/genericStyles.module.css";
import { useState, useEffect } from "react";
import GenericModal from "../GenericModal";
import NotificationConfigurationForm from "./NotificationConfigurationForm";
import { FormsIds, NOTIFICATION_CONFIG } from "../../Utils/Constants";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelCircleIcon from "@mui/icons-material/CancelOutlined";
import { getData } from "../../Services/getData";
import useCustomMemo from "../../Hooks/useCustomMemo";

export default function NotificationConfigurationTable() {
  const [contextValue, setContextValue] = useState(NOTIFICATION_CONFIG);
  const [openForm, setOpenForm] = useState(false);
  const [triggerReload, setTriggerReload] = useState(false);
  const [notificationTypes, setNotificationTypes] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  const memo = useCustomMemo();
  const roles = memo?.storage?.roles || [];

/**
 * Returns the name of a role given its id, or the id itself if the role is not found.
 * @param {string} roleId - The id of the role to search for.
 * @returns {string} - The name of the role if found, or the id itself if not found.
 */
  const getRoleName = (roleId: string) => {
    return roles.find((r: any) => r._id === roleId)?.name || roleId;
  };

/**
 * Returns the full name of a user given its id, or the id itself if user is not found.
 * @param {string} userId - The id of the user to search for.
 * @returns {string} - The full name of the user if found, or the id itself if not found.
 */
  const getUserName = (userId: string) => {
    const user = users.find((u: any) => u._id === userId);
    return user ? `${user.firstName} ${user.lastName}` : userId;
  };

  useEffect(() => {
    fetchData();
  }, []);

  /**
   * Fetches data for the notification configuration table, including
   * all available notification types and all users.
   */
  const fetchData = async () => {
    try {
      const typesResponse = await getData(
        `${endpoints.notifications.configurations}/types/all`
      );
      if (typesResponse?.data) {
        setNotificationTypes(typesResponse.data);
      }

      const usersResponse = await getData(endpoints.user.main);
      if (usersResponse?.data) {
        setUsers(usersResponse.data);
      }
    } catch (error) {
      console.error("Error fetching notification data:", error);
    }
  };

  /**
   * Creates column options for boolean columns with modern icon display
   */
  const getBooleanColumnOptions = () => ({
    setCellHeaderProps: () => ({ align: "center" }),
    setCellProps: () => ({ align: "center" }),
    customBodyRender: (value: boolean) => (
      <Box sx={{ display: "flex", justifyContent: "start" }}>
        {value ? (
          <CheckCircleIcon sx={{ color: "success.main", fontSize: "1.3rem" }} />
        ) : (
          <CancelCircleIcon sx={{ color: "error.main", fontSize: "1.3rem" }} />
        )}
      </Box>
    ),
  });

  /**
   * Defines the columns for the notification configuration table.
   */
  const columns = [
    {
      label: Vocabulary.type,
      name: "notificationType",
      options: {
        sort: false,
        customBodyRender: (value: string) => {
          const type = notificationTypes.find((t) => t.key === value);
          return type?.title;
        },
      },
    },
    {
      label: Vocabulary.sendEmail,
      name: "sendEmail",
      options: getBooleanColumnOptions(),
    },
    {
      label: Vocabulary.sendNotification,
      name: "sendNotification",
      options: getBooleanColumnOptions(),
    },
    {
      label: Vocabulary.sendSMS,
      name: "sendSMS",
      options: getBooleanColumnOptions(),
    },
    {
      label: Vocabulary.roles,
      name: "roles",
      options: {
        customBodyRender: (value: string[]) => (
          <>
            {value?.map((roleId) => (
              <Chip key={roleId} label={getRoleName(roleId)} size="small" />
            ))}
          </>
        ),
      },
    },
    {
      label: Vocabulary.users,
      name: "selectedUsers",
      options: {
        customBodyRender: (value: string[]) =>
          value?.length === 0 ? (
            <Chip label={Vocabulary.all} size="small" color="info" />
          ) : (
            <>
              {value?.map((userId) => (
                <Chip
                  key={userId}
                  label={getUserName(userId)}
                  size="small"
                  sx={{ mr: 0.5 }}
                />
              ))}
            </>
          ),
      },
    },
    {
      label: Vocabulary.active,
      name: "isActive",
      options: getBooleanColumnOptions(),
    },
  ];

  return (
    <div className={genericStyles.mainContainer}>
      <MUIDataTableCustomStyle
        onEditAction={ETableEditAction.MODAL}
        title={Vocabulary.notifications}
        columns={columns}
        CRUDEndPoint={endpoints.notifications.configurations}
        setContextValue={setContextValue}
        triggerReload={triggerReload}
        handleModalForm={setOpenForm}
        addOrEditForm={
          <GenericModal
            title={Vocabulary.editModalTitle}
            open={openForm}
            onClose={() => {
              setContextValue(NOTIFICATION_CONFIG);
              setOpenForm(false);
            }}
            maxWidth={EWidths.MD}
            width="800px"
            children={
              <NotificationConfigurationForm
                selectedRow={contextValue}
                onClose={() => {
                  setContextValue(NOTIFICATION_CONFIG);
                  setOpenForm(false);
                  setTriggerReload(!triggerReload);
                }}
                notificationTypes={notificationTypes}
                users={users}
              />
            }
            actions={
              <div className={genericStyles.editClientModalEditButton}>
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => setOpenForm(false)}
                  startIcon={<CancelIcon />}
                >
                  {Vocabulary.cancel}
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  form={FormsIds.notificationForm}
                  color="success"
                  startIcon={<SaveIcon />}
                >
                  {Vocabulary.save}
                </Button>
              </div>
            }
          />
        }
      />
    </div>
  );
}
